import type {
  EnvironmentId,
  ProjectId,
  PullRequestInvolvement,
  PullRequestListFilters as PullRequestListFilterSet,
  PullRequestListState,
} from "@afar-research/contracts";
import {
  CircleCheckIcon,
  CircleDashedIcon,
  CircleSlashIcon,
  CircleXIcon,
  EyeOffIcon,
  FolderGit2Icon,
  GitPullRequestDraftIcon,
  LayersIcon,
  ListFilterIcon,
  SearchIcon,
  TagIcon,
  UserRoundIcon,
} from "lucide-react";
import { type ElementType, useState } from "react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import {
  Menu,
  MenuCheckboxItem,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "../ui/menu";
import { Spinner } from "../ui/spinner";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import type { PullRequestAuthorFacet, PullRequestLabelFacet } from "./pullRequestListFacets.logic";
import { PullRequestActorAvatar, pullRequestLabelColor } from "./pullRequestPresentation";

export interface PullRequestFilterOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
  /** Uses the option's native icon tone. */
  readonly Icon: ElementType<{ className?: string }>;
  /** Why it cannot be chosen, carried onto the item as its tooltip. */
  readonly unavailable?: string | undefined;
}

export function PullRequestFilterOptionIcon<Value extends string>({
  option,
}: {
  readonly option: PullRequestFilterOption<Value>;
}) {
  return <option.Icon aria-hidden className="size-3.5" />;
}

export function PullRequestSearchInput({
  value,
  busy,
  onChange,
}: {
  readonly value: string;
  /** A search is on its way to the Environments, said where the typing is rather than over the list. */
  readonly busy?: boolean;
  readonly onChange: (value: string) => void;
}) {
  return (
    <InputGroup className="min-w-0 flex-1 **:[input]:h-9 sm:**:[input]:h-8">
      <InputGroupAddon>
        {busy ? <Spinner aria-hidden /> : <SearchIcon aria-hidden />}
      </InputGroupAddon>
      <InputGroupInput
        aria-label="Search Pull Requests"
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder="Search Pull Requests, or label:bug"
        type="search"
        value={value}
      />
    </InputGroup>
  );
}

/**
 * One filter's whole vocabulary behind a single compact trigger. Following T3Code: the label
 * rides beside the value so the condensed top bar reads as a sentence — "Open ▾ Authored ▾" —
 * rather than as a row of anonymous chips.
 */
export function CompactFilterMenu<Value extends string>({
  label,
  value,
  options,
  onChange,
  className,
  outlined = false,
  iconOnly = false,
  triggerIcon,
  triggerLabel,
}: {
  readonly label: string;
  readonly value: Value;
  readonly options: ReadonlyArray<PullRequestFilterOption<Value>>;
  readonly onChange: (value: Value) => void;
  readonly className?: string;
  readonly outlined?: boolean;
  readonly iconOnly?: boolean;
  readonly triggerIcon?: React.ReactNode;
  readonly triggerLabel?: string;
}) {
  const current = options.find((option) => option.value === value) ?? options[0];
  if (!current) return null;
  return (
    <Menu>
      <MenuTrigger
        render={<Button variant={outlined ? "outline" : "ghost"} size={outlined ? "sm" : "xs"} />}
        aria-label={label}
        className={className}
      >
        {triggerIcon ?? <PullRequestFilterOptionIcon option={current} />}
        {iconOnly ? (
          <span className="sr-only">{current.label}</span>
        ) : (
          <span className="truncate">
            {current.value === "" && triggerLabel !== undefined ? triggerLabel : current.label}
          </span>
        )}
      </MenuTrigger>
      <MenuPopup align="start" side="bottom" className="min-w-40">
        <PullRequestFilterRadioGroup
          label={label}
          onChange={onChange}
          options={options}
          value={value}
        />
      </MenuPopup>
    </Menu>
  );
}

/** MenuRadioGroup wants a string, so "every AFAR Project" wears a value no project id can be. */
const ALL_PROJECTS_VALUE = "all";
/** The same trick for the Environments, which are named by an id no empty string can collide with. */
const ALL_ENVIRONMENTS_VALUE = "";
/** The unset value of each narrowing group, which no filter of theirs is named after. */
const UNFILTERED_VALUE = "all";

/**
 * A Project's own radio value, carrying its Environment along with the id: the id alone is only
 * unique within its own Environment, so two rows sharing one would otherwise both read as checked.
 */
export const pullRequestProjectKey = (project: {
  readonly id: ProjectId;
  readonly environmentId: EnvironmentId;
}) => JSON.stringify([project.environmentId, project.id]);

const DRAFT_OPTIONS = [
  { value: UNFILTERED_VALUE, label: "All", Icon: LayersIcon },
  { value: "only", label: "Drafts only", Icon: GitPullRequestDraftIcon },
  { value: "hide", label: "Hide drafts", Icon: EyeOffIcon },
] as const satisfies ReadonlyArray<PullRequestFilterOption<string>>;

const REVIEW_OPTIONS = [
  { value: UNFILTERED_VALUE, label: "All", Icon: LayersIcon },
  { value: "approved", label: "Approved", Icon: CircleCheckIcon },
  { value: "changes-requested", label: "Changes requested", Icon: CircleXIcon },
  { value: "review-required", label: "Review required", Icon: CircleDashedIcon },
  { value: "none", label: "No reviews", Icon: CircleSlashIcon },
] as const satisfies ReadonlyArray<PullRequestFilterOption<string>>;

const CHECKS_OPTIONS = [
  { value: UNFILTERED_VALUE, label: "All", Icon: LayersIcon },
  { value: "passing", label: "Passing", Icon: CircleCheckIcon },
  { value: "failing", label: "Failing", Icon: CircleXIcon },
] as const satisfies ReadonlyArray<PullRequestFilterOption<string>>;

function PullRequestFilterRadioGroup<Value extends string>({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: Value;
  readonly options: ReadonlyArray<PullRequestFilterOption<Value>>;
  readonly onChange: (value: Value) => void;
}) {
  return (
    <MenuRadioGroup
      value={value}
      onValueChange={(next) => {
        if (next !== value) onChange(next as Value);
      }}
    >
      <MenuGroupLabel>{label}</MenuGroupLabel>
      {options.map((option) => {
        // An Environment that has already said it cannot read this is not a choice here:
        // offering it would answer the press by replacing a working list with that failure.
        const item = (
          <MenuRadioItem
            key={option.value}
            value={option.value}
            className={option.unavailable ? "data-disabled:pointer-events-auto" : undefined}
            disabled={option.unavailable !== undefined}
          >
            <span className="flex min-w-0 items-center gap-2">
              <PullRequestFilterOptionIcon option={option} />
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {option.unavailable ? <span className="shrink-0">· Unavailable</span> : null}
            </span>
          </MenuRadioItem>
        );
        if (!option.unavailable) return item;
        return (
          <Tooltip key={option.value}>
            <TooltipTrigger render={item} />
            <TooltipPopup side="top" className="max-w-80">
              {option.unavailable}
            </TooltipPopup>
          </Tooltip>
        );
      })}
    </MenuRadioGroup>
  );
}

function PullRequestFilterRadioSubmenu<Value extends string>({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: Value;
  readonly options: ReadonlyArray<PullRequestFilterOption<Value>>;
  readonly onChange: (value: Value) => void;
}) {
  const current = options.find((option) => option.value === value) ?? options[0];
  if (!current) return null;
  return (
    <MenuSub>
      <MenuSubTrigger>
        <PullRequestFilterOptionIcon option={current} />
        <span className="flex-1">{label}</span>
        <span className="min-w-0 max-w-32 truncate text-xs text-muted-foreground">
          {current.label}
        </span>
      </MenuSubTrigger>
      <MenuSubPopup className="min-w-56">
        <PullRequestFilterRadioGroup
          label={label}
          onChange={onChange}
          options={options}
          value={value}
        />
      </MenuSubPopup>
    </MenuSub>
  );
}

function PullRequestAuthorFilter({
  value,
  options,
  onChange,
}: {
  readonly value: string | undefined;
  readonly options: ReadonlyArray<PullRequestAuthorFacet>;
  readonly onChange: (author: string | undefined) => void;
}) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const login = value?.toLowerCase() ?? "";
  const selected = options.find((option) => option.actor.login.toLowerCase() === login);
  const visible = [
    ...(selected ? [selected] : []),
    ...options.filter(
      (option) =>
        option !== selected &&
        (needle.length === 0 ||
          option.actor.login.toLowerCase().includes(needle) ||
          (option.actor.name?.toLowerCase().includes(needle) ?? false)),
    ),
  ].slice(0, 10);
  const select = (next: string) => {
    if (next.toLowerCase() !== login) onChange(next || undefined);
  };
  return (
    <MenuSub>
      <MenuSubTrigger>
        <UserRoundIcon aria-hidden className="size-3.5" />
        <span className="flex-1">Author</span>
        <span className="min-w-0 max-w-32 truncate text-xs text-muted-foreground">
          {value ?? "Anyone"}
        </span>
      </MenuSubTrigger>
      <MenuSubPopup className="w-80">
        <div className="p-1 pb-2">
          <InputGroup>
            <InputGroupAddon>
              <SearchIcon aria-hidden />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search authors"
              autoFocus
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key !== "ArrowDown" && event.key !== "Escape") event.stopPropagation();
              }}
              placeholder="Search authors"
              size="sm"
              value={query}
            />
          </InputGroup>
        </div>
        <MenuRadioGroup value={selected?.actor.login ?? value ?? ""} onValueChange={select}>
          <MenuRadioItem value="">
            <span className="flex min-w-0 items-center gap-2">
              <LayersIcon aria-hidden className="size-3.5" />
              Anyone
            </span>
          </MenuRadioItem>
          {visible.map((option) => (
            <MenuRadioItem key={option.actor.login.toLowerCase()} value={option.actor.login}>
              <span className="flex min-w-0 items-center gap-2">
                <PullRequestActorAvatar actor={option.actor} />
                <span className="min-w-0 flex-1 truncate">{option.actor.login}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {option.mergedCount} merged
                </span>
              </span>
            </MenuRadioItem>
          ))}
          {visible.length === 0 ? <MenuItem disabled>No authors found</MenuItem> : null}
        </MenuRadioGroup>
      </MenuSubPopup>
    </MenuSub>
  );
}

function PullRequestLabelFilter({
  value,
  options,
  onChange,
}: {
  readonly value: ReadonlyArray<string>;
  readonly options: ReadonlyArray<PullRequestLabelFacet>;
  readonly onChange: (labels: ReadonlyArray<string>) => void;
}) {
  const selected = new Set(value.map((name) => name.toLowerCase()));
  const visible = [
    ...value
      .filter((name) => !options.some((option) => option.name.toLowerCase() === name.toLowerCase()))
      .map((name) => ({ name, color: null, count: 0 })),
    ...options,
  ];
  return (
    <MenuSub>
      <MenuSubTrigger>
        <TagIcon aria-hidden className="size-3.5" />
        <span className="flex-1">Labels</span>
        <span className="text-xs text-muted-foreground">
          {value.length === 0 ? "Any" : `${value.length} selected`}
        </span>
      </MenuSubTrigger>
      <MenuSubPopup className="w-72">
        {visible.length === 0 ? (
          <MenuItem disabled>No labels in this view</MenuItem>
        ) : (
          visible.map((option) => {
            const key = option.name.toLowerCase();
            const dot = pullRequestLabelColor(option.color);
            return (
              <MenuCheckboxItem
                key={key}
                checked={selected.has(key)}
                onCheckedChange={(next) =>
                  onChange(
                    next
                      ? [...value, option.name]
                      : value.filter((name) => name.toLowerCase() !== key),
                  )
                }
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden
                    className="size-2.5 shrink-0 rounded-full bg-muted-foreground"
                    {...(dot ? { style: { backgroundColor: dot } } : {})}
                  />
                  <span className="min-w-0 flex-1 truncate">{option.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {option.count}
                  </span>
                </span>
              </MenuCheckboxItem>
            );
          })
        )}
      </MenuSubPopup>
    </MenuSub>
  );
}

const NO_AUTHOR_FACETS: ReadonlyArray<PullRequestAuthorFacet> = [];
const NO_LABEL_FACETS: ReadonlyArray<PullRequestLabelFacet> = [];

/**
 * Every narrowing of the list behind one control, separate from sorting. Following T3Code: the
 * trigger carries a count whenever any filter is off its default, so a narrowed list is never a
 * mystery, and each group is a submenu rather than a field in a form.
 */
export function PullRequestFiltersMenu({
  onOpenChange,
  state,
  stateOptions,
  onState,
  involvement,
  involvementOptions,
  onInvolvement,
  filters,
  onFilters,
  authorOptions = NO_AUTHOR_FACETS,
  labelOptions = NO_LABEL_FACETS,
  environment,
  environmentOptions,
  onEnvironment,
  projects,
  projectId,
  projectEnvironmentId,
  unavailable,
  onProject,
}: {
  readonly onOpenChange?: (open: boolean) => void;
  readonly state: PullRequestListState;
  readonly stateOptions: ReadonlyArray<PullRequestFilterOption<PullRequestListState>>;
  readonly onState: (state: PullRequestListState) => void;
  readonly involvement: PullRequestInvolvement;
  readonly involvementOptions: ReadonlyArray<PullRequestFilterOption<PullRequestInvolvement>>;
  readonly onInvolvement: (involvement: PullRequestInvolvement) => void;
  /** The narrowings beyond state and involvement; an absent field is that group unfiltered. */
  readonly filters: PullRequestListFilterSet;
  readonly onFilters: (filters: PullRequestListFilterSet) => void;
  readonly authorOptions?: ReadonlyArray<PullRequestAuthorFacet>;
  readonly labelOptions?: ReadonlyArray<PullRequestLabelFacet>;
  readonly environment: EnvironmentId | undefined;
  /**
   * Includes the "all Environments" entry, whose value is the empty string. With one Environment
   * there is nothing to switch between, so the whole group stays out of the menu.
   */
  readonly environmentOptions: ReadonlyArray<PullRequestFilterOption<string>>;
  readonly onEnvironment: (environment: EnvironmentId | undefined) => void;
  /** Every admitted AFAR Project, each carrying the Environment it belongs to. */
  readonly projects: ReadonlyArray<{
    readonly id: ProjectId;
    readonly environmentId: EnvironmentId;
    readonly title: string;
  }>;
  readonly projectId: ProjectId | undefined;
  /**
   * The Environment the selected AFAR Project belongs to. A project id is only unique within its
   * own Environment, so without this two rows sharing an id would both read as checked here.
   */
  readonly projectEnvironmentId: EnvironmentId | undefined;
  /**
   * AFAR Projects whose repository could not be read this time round. They are named here, where
   * the reader is already choosing between projects, rather than as a count above the list that
   * says something is missing without saying which.
   */
  readonly unavailable: ReadonlyMap<string, string>;
  /** The Environment comes with the project id, since picking a row picks one Environment's copy of it. */
  readonly onProject: (
    projectId: ProjectId | undefined,
    environmentId: EnvironmentId | undefined,
  ) => void;
}) {
  const selectedLabels = (filters.labels ?? []).flatMap((group) => group);
  const filterCount = [
    state !== "open",
    involvement !== "all",
    environment,
    projectId,
    filters.draft,
    filters.review,
    filters.checks,
    filters.author,
    ...selectedLabels,
  ].filter(Boolean).length;
  const updateFilters = (next: Partial<PullRequestListFilterSet>) =>
    onFilters(
      Object.fromEntries(
        Object.entries({ ...filters, ...next }).filter(([, value]) => value !== undefined),
      ) as PullRequestListFilterSet,
    );
  const updateFilter = (key: keyof PullRequestListFilterSet, value: string) =>
    updateFilters({
      [key]: value === UNFILTERED_VALUE ? undefined : value,
    } as Partial<PullRequestListFilterSet>);
  const projectValue =
    projectId === undefined || projectEnvironmentId === undefined
      ? ALL_PROJECTS_VALUE
      : pullRequestProjectKey({ id: projectId, environmentId: projectEnvironmentId });
  const projectOptions: ReadonlyArray<PullRequestFilterOption<string>> = [
    { value: ALL_PROJECTS_VALUE, label: "All AFAR Projects", Icon: LayersIcon },
    ...projects
      .toSorted(
        (left, right) =>
          Number(unavailable.has(pullRequestProjectKey(left))) -
          Number(unavailable.has(pullRequestProjectKey(right))),
      )
      .map((project) => ({
        value: pullRequestProjectKey(project),
        label: project.title,
        Icon: FolderGit2Icon,
        ...(unavailable.has(pullRequestProjectKey(project))
          ? { unavailable: unavailable.get(pullRequestProjectKey(project)) }
          : {}),
      })),
  ];
  return (
    <Menu onOpenChange={onOpenChange}>
      <MenuTrigger
        render={
          <Button
            className={cn(filterCount > 0 && "[--control-icon-color:currentColor]")}
            variant="outline"
            size="sm"
          />
        }
      >
        <ListFilterIcon className="size-4" />
        <span>Filters</span>
        {filterCount > 0 ? (
          <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
            {filterCount}
          </span>
        ) : null}
      </MenuTrigger>
      <MenuPopup align="end" side="bottom" className="w-56">
        <PullRequestFilterRadioSubmenu
          label="State"
          onChange={onState}
          options={stateOptions}
          value={state}
        />
        <PullRequestFilterRadioSubmenu
          label="Involvement"
          onChange={onInvolvement}
          options={involvementOptions}
          value={involvement}
        />
        <MenuSeparator />
        <PullRequestAuthorFilter
          onChange={(author) => updateFilters({ author })}
          options={authorOptions}
          value={filters.author}
        />
        <PullRequestLabelFilter
          onChange={(labels) =>
            updateFilters({
              labels: labels.length === 0 ? undefined : labels.slice(0, 10).map((label) => [label]),
            })
          }
          options={labelOptions}
          value={selectedLabels}
        />
        <PullRequestFilterRadioSubmenu
          label="Draft"
          onChange={(draft) => updateFilter("draft", draft)}
          options={DRAFT_OPTIONS}
          value={filters.draft ?? UNFILTERED_VALUE}
        />
        <PullRequestFilterRadioSubmenu
          label="Review"
          onChange={(review) => updateFilter("review", review)}
          options={REVIEW_OPTIONS}
          value={filters.review ?? UNFILTERED_VALUE}
        />
        <PullRequestFilterRadioSubmenu
          label="Checks"
          onChange={(checks) => updateFilter("checks", checks)}
          options={CHECKS_OPTIONS}
          value={filters.checks ?? UNFILTERED_VALUE}
        />
        {environmentOptions.length > 2 ? (
          <>
            <MenuSeparator />
            <PullRequestFilterRadioSubmenu
              label="Environment"
              onChange={(next) =>
                onEnvironment(next === ALL_ENVIRONMENTS_VALUE ? undefined : (next as EnvironmentId))
              }
              options={environmentOptions}
              value={environment ?? ALL_ENVIRONMENTS_VALUE}
            />
          </>
        ) : null}
        <MenuSeparator />
        <PullRequestFilterRadioSubmenu
          label="AFAR Project"
          onChange={(next) => {
            const project = projects.find((candidate) => pullRequestProjectKey(candidate) === next);
            if (project) onProject(project.id, project.environmentId);
            else if (projectId !== undefined) onProject(undefined, undefined);
          }}
          options={projectOptions}
          value={projectValue}
        />
      </MenuPopup>
    </Menu>
  );
}
